import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import axios from "axios";

// ── Types ──────────────────────────────────────────────────────────────────────

export interface CsvRow {
  code: string;
  credits: number;
  devise: string;
  reference: string;
  dateDistribution: string;
}

export interface XlsRow {
  umn: string;
  uuid: string | null;
  phone: string | null;
  amount: number | null;
  balance: number | null;
  issueDate: string | null;
  expiryDate: string | null;
}

export interface DbRow {
  saralId: string | null;
  code: string | null;
  state: string | null;
  amount: string | null;
  programName: string | null;
  principalName: string | null;
  distributorName: string | null;
  // SELP-specific
  currency?: string | null;
  voucherRef?: string | null;
  createdAtUtc?: string | null;
  // ICICI-specific
  voucherUuid?: string | null;
  availableAmount?: string | null;
  beneficiaryPhone?: string | null;
  voucherCreateDate?: string | null;
  expiryDate?: string | null;
}

export interface Discrepancy {
  discrepancyType: string;
  voucherCode: string | null;
  saralId: string | null;
  // SELP fields
  csvRowNumber: number | null;
  csvValue: string | null;
  dbValue: string | null;
  csvRow: CsvRow | null;
  firstOccurrenceCsvRow: CsvRow | null;
  // ICICI fields
  xlsRowNumber: number | null;
  xlsRow: XlsRow | null;
  firstOccurrenceXlsRow: XlsRow | null;
  dbRow: DbRow | null;
}

export interface ReconSummary {
  processorType: string;
  fileName: string;
  dateFrom: string;
  dateTo: string;
  fileDateFrom: string | null;
  fileDateTo: string | null;
  totalCsvRows?: number;
  totalXlsRows?: number;
  totalDbRows: number;
  matchedCount: number;
  discrepancyCount: number;
  byType: Record<string, number>;
  principalId?: number;
}

export interface ReconResult {
  summary: ReconSummary;
  discrepancies: Discrepancy[];
  processorRows: CsvRow[] | XlsRow[];
  dbRows: DbRow[];
}

interface ReconState {
  loading: boolean;
  error: string | null;
  result: ReconResult | null;
}

const initialState: ReconState = {
  loading: false,
  error: null,
  result: null,
};

// ── Thunk ──────────────────────────────────────────────────────────────────────

export const runRecon = createAsyncThunk(
  "recon/runRecon",
  async (
    {
      file,
      processor,
      principalId,
      fromDate,
      toDate,
    }: {
      file: File;
      processor: string;
      principalId?: number;
      fromDate: string;
      toDate: string;
    },
    { rejectWithValue }
  ) => {
    const formData = new FormData();
    formData.append("file", file);

    const token = localStorage.getItem("accessToken");

    let url = `${window.env.API_URL}/v1/programs/recon/sessions?processor=${processor.toUpperCase()}&fromDate=${fromDate}&toDate=${toDate}`;
    if (principalId !== undefined) url += `&principalId=${principalId}`;

    try {
      const response = await axios.post(url, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
        timeout: 120000,
      });
      return response.data as ReconResult;
    } catch (error: any) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "Reconciliation failed";
      return rejectWithValue(message);
    }
  }
);

// ── Slice ──────────────────────────────────────────────────────────────────────

const reconSlice = createSlice({
  name: "recon",
  initialState,
  reducers: {
    clearReconResult(state) {
      state.result = null;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(runRecon.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.result = null;
      })
      .addCase(runRecon.fulfilled, (state, action: PayloadAction<ReconResult>) => {
        state.loading = false;
        state.result = action.payload;
      })
      .addCase(runRecon.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearReconResult } = reconSlice.actions;
export default reconSlice.reducer;
